// src/Pages/SalesManagement.js

import React, { useState, useEffect } from 'react';
import api from '../api/api.js'; // Tu instancia de axios
import styles from './SalesManagement.module.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useToast } from '../Components/ui/Toast.jsx';
import { useConfirm } from '../Components/ui/ConfirmDialog.jsx';


const SalesManagement = () => {
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  // —— Estados principales ——
  const [tickets, setTickets] = useState([]);    // Todos los tickets
  const [trips, setTrips] = useState([]);        // Todas las rutas
  const [loading, setLoading] = useState(true);  // Indicador de carga
  const [error, setError] = useState(null);      // Mensaje de error
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);

  // Búsqueda y paginación
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;                                 // Filas por página

  // Estado para resumen mensual
  const [availableMonths, setAvailableMonths] = useState([]); 
  const [selectedMonth, setSelectedMonth] = useState('');     
  const [monthlyRevenue, setMonthlyRevenue] = useState(0);
  const [selectedTripId, setSelectedTripId] = useState('');
  const [topRoutes, setTopRoutes] = useState([]);
  const [paymentRevenue, setPaymentRevenue] = useState([]);

  // Estado para modal de cancelación
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTicketId, setCancelTicketId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');


    

  // —— 1) Cargar datos al montar ——
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // A) Obtener rutas (trip) → para mostrar origen/destino
        const tripsRes = await api.get('/courses');
        const tripsData = Array.isArray(tripsRes.data) ? tripsRes.data : [];
        setTrips(tripsData);

        // B) Obtener bookings
        const ticketsRes = await api.get('/booking');
        const ticketsData = Array.isArray(ticketsRes.data) ? ticketsRes.data : [];
        setTickets(ticketsData);

        // C)obtener clientes
        const clientsRes = await api.get('/client');  
        setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);

        // 🆕 D) Obtener usuarios (para obtener los correos)
      const usersRes = await api.get('/user');
      setUsers(Array.isArray(usersRes.data.users) ? usersRes.data.users : []);

        // E) Extraer meses únicos ("YYYY-MM") para el dropdown de resumen
        const monthsSet = new Set();
        ticketsData.forEach((t) => {
          if (t.date) {
            const d = new Date(t.date);
            if (!isNaN(d)) {
              const year = d.getFullYear();
              const month = String(d.getMonth() + 1).padStart(2, '0');
              monthsSet.add(`${year}-${month}`);
            }
          }
        });

        // F) Rutas más rentables (no bloquea si el endpoint aún no está disponible)
        try {
          const topRoutesRes = await api.get('/top_routes');
          setTopRoutes(Array.isArray(topRoutesRes.data) ? topRoutesRes.data : []);
        } catch { setTopRoutes([]); }

        // G) Ganancias por método de pago
        try {
          const revenueRes = await api.get('/revenue_by_payment');
          setPaymentRevenue(Array.isArray(revenueRes.data) ? revenueRes.data : []);
        } catch { setPaymentRevenue([]); }

        const monthsArray = Array.from(monthsSet).sort((a, b) => (a < b ? 1 : -1));
        setAvailableMonths(monthsArray);
        if (monthsArray.length > 0) setSelectedMonth(monthsArray[0]);
        else setSelectedMonth('');
      } catch (err) {



        setError('Hubo un problema al cargar las reservas.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // —— 2) Recalcular ganancias mensuales cuando cambie tickets o mes seleccionado ——
  useEffect(() => {
    if (!selectedMonth) {
      setMonthlyRevenue(0);
      return;
    }
    const total = tickets
      .filter((t) => {
        if (Number(t.active) !== 1) return false; // Solo activos
        if (!t.date) return false;
        const d = new Date(t.date);
        if (isNaN(d)) return false;
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}` === selectedMonth;
      })
      .reduce((sum, t) => {
        const p = parseFloat(t.price) || 0;
        return sum + p;
      }, 0);
    setMonthlyRevenue(total);
  }, [tickets, selectedMonth]);

  // —— 3) Filtrar tickets por mes y searchQuery ——
  const filteredTickets = tickets.filter((t) => {
      // Filtrar por mes seleccionado
      if (selectedMonth) {
        if (!t.date) return false;
        const d = new Date(t.date);
        if (isNaN(d)) return false;
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        if (`${year}-${month}` !== selectedMonth) return false;
      }
      // 🆕 Filtrar por ruta seleccionada
       if (selectedTripId && Number(t.trip_id) !== Number(selectedTripId)) return false;
      // Filtrar por código de reserva (reservation_code)
      if (searchQuery.trim() === '') return true;
      return String(t.reservation_code || '')
        .toLowerCase()
        .includes(searchQuery.trim().toLowerCase());
    });

  // —— 4) Paginación ——
  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / rowsPerPage));
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginatedTickets = filteredTickets.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // —— 5) "Activar Pago y Enviar Email" ——
  const handleActivatePayment = async () => {
    if (!selectedTicketId || !selectedPaymentMethod) {
      showToast('Selecciona un método de pago.', { type: 'warning' });
      return;
    }

    try {
      await api.put(`/pay/${selectedTicketId}`, { payment_method: selectedPaymentMethod });

      setTickets((prev) =>
        prev.map((t) =>
          Number(t.id) === Number(selectedTicketId)
            ? { ...t, payment_method: selectedPaymentMethod, Status: 'confirm' }
            : t
        )
      );

      showToast('Pago confirmado y ticket enviado al cliente por email.', { type: 'success' });
    } catch (err) {
      showToast('No se pudo confirmar el pago. Intenta de nuevo.', { type: 'error' });
    } finally {
      setShowPaymentModal(false);
      setSelectedTicketId(null);
      setSelectedPaymentMethod('');
      setSelectedTicket(null);
    }
  };

  // —— 6) "Cancelar Reserva" ——
  const openCancelModal = (ticketId) => {
    setCancelTicketId(ticketId);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleCancelReservation = async () => {
    if (!cancelReason.trim()) {
      showToast('Por favor, ingresa una razón para la cancelación.', { type: 'warning' });
      return;
    }
    try {
      await api.delete(`/booking/${cancelTicketId}`, {
        data: { cancel_reason: cancelReason }
      });
      setTickets((prev) =>
        prev.map((t) =>
          Number(t.id) === Number(cancelTicketId)
            ? { ...t, active: 0, cancel_reason: cancelReason }
            : t
        )
      );
      showToast('Reserva cancelada.', { type: 'success' });
      setShowCancelModal(false);
      setCancelTicketId(null);
      setCancelReason('');
    } catch (err) {
      showToast('No se pudo cancelar la reserva. Intenta de nuevo.', { type: 'error' });
    }
  };

  // —— 7) "Liberar asiento" (reservas expiradas sin pago) ——
  const handleReleaseExpired = async (bookingId) => {
    const ok = await confirm('¿Liberar el asiento de esta reserva expirada? Se cancelará y el asiento quedará disponible.', {
      title: 'Confirmar liberación',
      confirmText: 'Liberar',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/booking/${bookingId}`, {
        data: { cancel_reason: 'Tiempo de pago agotado - liberado por admin' }
      });
      setTickets(prev => prev.map(t =>
        Number(t.id) === Number(bookingId) ? { ...t, active: 0 } : t
      ));
      showToast('Asiento liberado correctamente.', { type: 'success' });
    } catch (err) {
      showToast('No se pudo liberar el asiento. Intenta de nuevo.', { type: 'error' });
    }
  };

  //8 descarga de excel con los datos
  const exportToExcel = () => {
  const rows = filteredTickets.map((t) => {
    const { origin, destination } = getTripInfo(t.trip_id);

    const d = new Date(t.date);
    const dateStr = !isNaN(d)
      ? `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
          .toString()
          .padStart(2, '0')}/${d.getFullYear()} ${d.getHours()
          .toString()
          .padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
      : t.date;

    // Determinar estado para Excel
    let estadoExcel = '';
    if (Number(t.active) === 0) {
      estadoExcel = 'Cancelada';
    } else if (t.Status === 'confirm') {
      estadoExcel = 'Pagada';
    } else if (t.Status === 'waiting' && t.payment_method) {
      estadoExcel = 'Esperando Confirmacion';
    } else if (t.Status === 'waiting' && !t.payment_method) {
      estadoExcel = 'Pendiente de pago';
    } else {
      estadoExcel = 'Pendiente';
    }

    return {
      ID: t.id,
      Código: t.reservation_code,
      Cliente: t.client_id,
      Ruta: `${origin} → ${destination}`,
      Fecha: dateStr,
      Asientos: t.seat_quant,
      Precio: `$${parseFloat(t.price).toFixed(2)}`,
      Pago: t.payment_method || '—',
      Estado: estadoExcel,
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Tickets');

  const blob = XLSX.write(workbook, { type: 'binary', bookType: 'xlsx' });
  const buffer = new ArrayBuffer(blob.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < blob.length; i++) {
    view[i] = blob.charCodeAt(i) & 0xff;
  }

  const blobData = new Blob([buffer], { type: 'application/octet-stream' });
  saveAs(blobData, `tickets_${selectedMonth}.xlsx`);
};
  // —— 8) Obtener datos de la ruta (origen/destino) por trip_id ——
  const getTripInfo = (tripId) => {
    const trip = trips.find((r) => Number(r.id) === Number(tripId));
    if (!trip) return { origin: '—', destination: '—' };
    return { origin: trip.departure, destination: trip.arrival };
  };

  // —— 9) Handlers búsqueda y QR ——
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const getClientEmail = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    if (!client) return '—';
    const user = users.find((u) => u.id === client.user_id);
    return user ? user.mail : '—';
  };

  // —— UI ——
  if (loading) {
    return (
      <div className={styles.container}>
        <h2>Gestión de Ventas</h2>
        <p>Cargando datos…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className={styles.container}>
        <h2>Gestión de Ventas</h2>
        <p className={styles.errorText}>{error}</p>
      </div>
    );
  }
  return (
    <div className={styles.container}>
      <h2>Gestión de Ventas</h2>
{showPaymentModal && (
  <div className={styles.modalOverlay}>
    <div className={styles.modalContent}>
      <h3>Confirmar Pago y Enviar Ticket</h3>
      <p style={{ fontSize: '14px', color: '#555', marginBottom: '12px' }}>
        Al confirmar, se registrará el pago y se enviará el ticket al cliente por email.
      </p>

      {/* Comprobante subido por el cliente */}
      {selectedTicket?.capture_path && (
        <div style={{ marginBottom: '14px' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Comprobante del cliente:</p>
          {/\.(jpg|jpeg|png|gif|webp)$/i.test(selectedTicket.capture_path) ? (
            <img
              src={selectedTicket.capture_path}
              alt="Comprobante"
              style={{ maxWidth: '100%', maxHeight: '220px', borderRadius: '6px', border: '1px solid #ddd', objectFit: 'contain' }}
            />
          ) : (
            <a href={selectedTicket.capture_path} target="_blank" rel="noopener noreferrer"
               style={{ color: '#1d72b8', fontSize: '13px' }}>
              Ver comprobante (PDF)
            </a>
          )}
        </div>
      )}

      {/* Método reportado por el cliente */}
      {selectedTicket?.payment_method && (
        <p style={{ fontSize: '13px', marginBottom: '10px' }}>
          Método reportado por el cliente: <strong>{selectedTicket.payment_method}</strong>
        </p>
      )}

      <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
        Confirmar método de pago:
      </label>
      <select
        value={selectedPaymentMethod}
        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
        className={styles.modalSelect}
      >
        <option value="">-- Selecciona método de pago --</option>
        <option value="Zelle">Zelle</option>
        <option value="PagoMovil">Pago Móvil</option>
        <option value="USDT">USDT (TRC20)</option>
        <option value="PayPal">PayPal</option>
        <option value="Efectivo">Efectivo</option>
      </select>

      <div className={styles.modalButtons}>
        <button className={styles.confirmButton} onClick={handleActivatePayment}>
          Confirmar Pago y Enviar Email
        </button>
        <button
          className={styles.cancelButton}
          onClick={() => {
            setShowPaymentModal(false);
            setSelectedPaymentMethod('');
            setSelectedTicketId(null);
            setSelectedTicket(null);
          }}
        >
          Cancelar
        </button>
      </div>
    </div>
  </div>
)}

{/* Modal de Cancelación */}
{showCancelModal && (
  <div className={styles.modalOverlay}>
    <div className={styles.modalContent}>
      <h3>Cancelar Reserva</h3>
      <p>Por favor, indica la razón de la cancelación:</p>
      <textarea
        value={cancelReason}
        onChange={(e) => setCancelReason(e.target.value)}
        placeholder="Escribe la razón de cancelación..."
        className={styles.modalTextarea}
        rows={4}
        style={{ width: '100%', padding: '10px', marginBottom: '15px', borderRadius: '5px', border: '1px solid #ccc' }}
      />
      <div className={styles.modalButtons}>
        <button
          className={styles.confirmButton}
          onClick={handleCancelReservation}
          style={{ backgroundColor: '#dc3545' }}
        >
          Confirmar Cancelación
        </button>
        <button
          className={styles.cancelButton}
          onClick={() => {
            setShowCancelModal(false);
            setCancelTicketId(null);
            setCancelReason('');
          }}
        >
          Volver
        </button>
      </div>
    </div>
  </div>
)}

      {/* ——————— Resumen Mensual ——————— */}
      <div className={styles.summaryContainer}>
        <label htmlFor="monthSelect"><strong>Mes:</strong></label>
        <select
          id="monthSelect"
          value={selectedMonth}
          onChange={(e) => {
            setSelectedMonth(e.target.value);
            setCurrentPage(1);
          }}
          className={styles.monthSelect}
        >
          {availableMonths.map((m) => {
            const [year, month] = m.split('-');
            return (
              <option key={m} value={m}>
                {month}/{year}
              </option>
            );
          })}
        </select>

        {/* 🆕 Filtro por Ruta */}
            <label htmlFor="tripSelect" style={{ marginLeft: '20px' }}>
                <strong>Ruta:</strong>
            </label>
            <select
                id="tripSelect"
                value={selectedTripId}
                onChange={(e) => {
                setSelectedTripId(e.target.value);
                setCurrentPage(1);
                }}
                className={styles.monthSelect}
            >
                <option value="">Todas las rutas</option>
                {Array.from(
                  new Map(
                    tickets
                      .filter(t => t.trip_id && t.origin && t.destination)
                      .map(t => [t.trip_id, { id: t.trip_id, origin: t.origin, destination: t.destination }])
                  ).values()
                ).map((route) => (
                  <option key={route.id} value={route.id}>
                    {route.origin} → {route.destination}
                  </option>
                ))}
            </select>

            <button
                onClick={() => setSelectedTripId('')}
                className={styles.buttonClear}
                style={{ marginLeft: '10px' }}
            >
                Limpiar Ruta
            </button>

        <div className={styles.revenueBox}>
          <span>Ganancias Mensuales:</span>
          <span className={styles.revenueAmount}>
            ${monthlyRevenue.toFixed(2)}
          </span>
        </div>
        <button onClick={exportToExcel} className={styles.exportButton}>
          Descargar Excel
        </button>
      </div>

     
      {/* ——————— Búsqueda por Código / Lector QR ——————— */}
      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder="Buscar por código de reserva"
          value={searchQuery}
          onChange={handleSearchChange}
          className={styles.searchInput}
        />
      </div>

      {/* ——————— Tabla paginada de Reservas ——————— */}
      <div className={styles.tableContainer}>
        <table className={styles.ticketsTable}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Código Reserva</th>
              <th>Correo Cliente</th>
              <th>Ruta</th>
              <th>Fecha Reserva</th>
              <th>Asientos</th>
              <th>Precio</th>
              <th>Pago</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginatedTickets.map((t) => {
              // Usar origin/destination directo del backend, o buscar en trips como fallback
              const origin = t.origin || getTripInfo(t.trip_id).origin;
              const destination = t.destination || getTripInfo(t.trip_id).destination;

              // Estado del ticket basado en Status, active y payment_method
              let stateLabel = '';
              if (Number(t.active) === 0) {
                stateLabel = 'Cancelada';
              } else if (t.Status === 'confirm') {
                stateLabel = 'Pagada';
              } else if (t.Status === 'waiting' && t.payment_method) {
                stateLabel = 'Esperando Confirmacion';
              } else if (t.Status === 'waiting' && !t.payment_method) {
                stateLabel = 'Pendiente de pago';
              } else {
                stateLabel = 'Pendiente';
              }

              // Formatear fecha a "DD/MM/YYYY HH:MM"
              let formattedDate = '—';
              if (t.date) {
                const d = new Date(t.date);
                if (!isNaN(d)) {
                  const day = String(d.getDate()).padStart(2, '0');
                  const month = String(d.getMonth() + 1).padStart(2, '0');
                  const year = d.getFullYear();
                  const hours = String(d.getHours()).padStart(2, '0');
                  const minutes = String(d.getMinutes()).padStart(2, '0');
                  formattedDate = `${day}/${month}/${year} ${hours}:${minutes}`;
                } else {
                  formattedDate = t.date;
                }
              }

              return (
                <tr key={t.id}>
                  <td>{t.id}</td>
                  <td>{t.reservation_code || '—'}</td>
                  <td>{t.client_email || getClientEmail(t.client_id)}</td>
                  <td>{origin} → {destination}</td>
                  <td>{formattedDate}</td>
                  <td style={{ textAlign: 'center' }}>{t.seat_quant}</td>
                  <td>${parseFloat(t.price).toFixed(2)}</td>
                  <td>{t.payment_method || '—'}</td>
                  <td>
                    {stateLabel}
                    {Number(t.active) === 0 && t.cancel_reason && (
                      <div style={{ fontSize: '11px', color: '#dc3545', marginTop: '4px' }}>
                        Razón: {t.cancel_reason}
                      </div>
                    )}
                  </td>
                  <td className={styles.actionsCell}>
                    {/* Liberar asiento: reserva sin pago y expirada (>15 min) */}
                    {Number(t.active) === 1 && t.Status === 'waiting' && !t.payment_method &&
                      (Date.now() - new Date(t.date).getTime()) > 15 * 60 * 1000 && (
                      <button
                        className={`${styles.actionButton} ${styles.cancelButton}`}
                        style={{ background: '#8B0000', color: '#fff' }}
                        onClick={() => handleReleaseExpired(t.id)}
                      >
                        Liberar asiento
                      </button>
                    )}

                    {/* Activar Pago: solo si tiene comprobante subido (payment_method set) y no es Stripe */}
                    {Number(t.active) === 1 && t.Status === 'waiting' && t.payment_method && t.payment_method !== 'Stripe' && (
                      <button
                        className={`${styles.actionButton} ${styles.activateButton}`}
                        onClick={() => {
                          setSelectedTicketId(t.id);
                          setSelectedTicket(t);
                          setSelectedPaymentMethod(t.payment_method || '');
                          setShowPaymentModal(true);
                        }}
                      >
                        Activar Pago
                      </button>
                    )}

                    {/* Botón para cancelar reserva (solo si está activa) */}
                    {Number(t.active) === 1 && (
                      <button
                        className={`${styles.actionButton} ${styles.cancelButton}`}
                        onClick={() => openCancelModal(t.id)}
                      >
                        Cancelar Reserva
                      </button>
                    )}

                    {/* Indicador si está cancelada */}
                    {Number(t.active) === 0 && (
                      <span className={styles.inactiveText}>—</span>
                    )}

                    {/* Checkmark si el pago ya fue confirmado */}
                    {Number(t.active) === 1 && t.Status === 'confirm' && (
                      <span className={styles.paidText}>Confirmada</span>
                    )}

                    {/* Mostrar comprobante si existe */}
                    {t.capture_path && (
                     <button
                       className={`${styles.actionButton} ${styles.viewButton}`}
                       onClick={() => {
                         const receiptUrl = t.capture_path.startsWith('http')
                           ? t.capture_path
                           : `${window.location.hostname === 'localhost' ? 'http://localhost:3001' : 'https://proyecto-flamingo-backend.onrender.com'}/uploads/${t.capture_path}`;
                         window.open(receiptUrl, '_blank');
                       }}
                     >
                        Ver Comprobante
                     </button>
                    )}

                  </td>
                </tr>
              );
            })}
            {paginatedTickets.length === 0 && (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
                  No hay reservas que coincidan con tu búsqueda.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    
      <div className={styles.topRoutesContainer}>
        <h3> Rutas que Más Generan</h3>
        <table className={styles.topRoutesTable}>
          <thead>
            <tr>
              <th>#</th>
              <th>Ruta</th>
              <th>Ganancia Total</th>
              <th>Tickets Vendidos</th>
              <th>Progreso</th>
            </tr>
          </thead>
          <tbody>
            {topRoutes.map((r, idx) => {
              const maxRevenue = Math.max(...topRoutes.map(rt => rt.total_ganado));
              const percent = (r.total_ganado / maxRevenue) * 100;
              return (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{r.departure} → {r.arrival}</td>
                  <td>${parseFloat(r.total_ganado).toFixed(2)}</td>
                  <td>{r.tickets_vendidos}</td>
                  <td>
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{ width: `${percent}%` }}
                      ></div>
                    </div>
                    <span>{percent.toFixed(0)}%</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>


      <div className={styles.paymentRevenueContainer}>
        <h3> Ganancias por Tipo de Pago</h3>
        <table className={styles.paymentRevenueTable}>
          <thead>
            <tr>
              <th>Método</th>
              <th>Total Ganado</th>
            </tr>
          </thead>
          <tbody>
            {paymentRevenue.map((p, idx) => (
              <tr key={idx}>
                <td>{p.payment_method || '—'}</td>
                <td>${parseFloat(p.total_ganado).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>


      {/* ——————— Controles de Paginación ——————— */}
      {filteredTickets.length > rowsPerPage && (
        <div className={styles.paginationContainer}>
          <button
            className={styles.pageButton}
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            ← Anterior
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
            <button
              key={num}
              className={`${styles.pageButton} ${
                currentPage === num ? styles.activePage : ''
              }`}
              onClick={() => setCurrentPage(num)}
            >
              {num}
            </button>
          ))}
          <button
            className={styles.pageButton}
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};

export default SalesManagement;